import type { QueryKey } from '@tanstack/react-query';

// Related reads include embedded records, filtered searches, and parent limits.
// These are direct dependencies, not a transitive invalidation graph.
const relatedQueryKeys: Record<string, QueryKey[]> = {
    spaces: [['organizations', 'limits']],
    organizations: [
        ['organization-profiles'],
        ['organization-profile-jobs'],
        ['organization-profile-users'],
        ['event-sessions'],
        ['users'],
        ['saved-organizations'],
        ['spaces', 'limits'],
    ],
    'organization-profiles': [
        ['organization-profile-jobs'],
        ['organization-profile-users'],
        ['event-sessions'],
        ['events', 'list'],
        ['events', 'limits'],
        ['events', 'analytics'],
    ],
    jobs: [
        ['organization-profile-jobs'],
        ['organization-profiles'],
        ['saved-jobs'],
        ['organizations', 'limits'],
        ['events', 'analytics'],
    ],
    'organization-profile-jobs': [['organization-profiles'], ['events', 'analytics']],
    'organization-profile-users': [['organization-profiles'], ['events', 'analytics']],
    users: [
        ['user-profiles'],
        ['organization-profile-users'],
        ['organization-profiles'],
        ['event-attendees'],
        ['event-session-users'],
        ['event-sessions'],
        ['conversations'],
        ['messages'],
        ['saved-users'],
        ['user-notes'],
        ['organizations', 'limits'],
        ['spaces', 'limits'],
        ['events', 'limits'],
        ['events', 'analytics'],
    ],
    'user-profiles': [['users'], ['events', 'analytics']],
    educations: [['user-profiles'], ['users']],
    experiences: [['user-profiles'], ['users']],
    events: [['event-sessions'], ['spaces', 'limits']],
    'event-attendees': [
        ['events', 'limits'],
        ['events', 'list'],
        ['events', 'analytics'],
        ['user-profiles', 'search'],
    ],
    'event-sessions': [
        ['event-session-users'],
        ['saved-event-sessions'],
        ['events'],
        ['organization-profiles', 'limits'],
    ],
    'event-session-users': [['event-sessions'], ['events', 'analytics'], ['user-profiles', 'search']],
    conversations: [['messages'], ['events', 'analytics']],
    messages: [['conversations'], ['events', 'analytics']],
    'saved-users': [
        ['user-profiles', 'search'],
        ['events', 'analytics'],
    ],
    'saved-organizations': [
        ['organization-profiles', 'search'],
        ['events', 'analytics'],
    ],
    'saved-jobs': [
        ['organization-profile-jobs', 'search'],
        ['events', 'analytics'],
    ],
    'saved-event-sessions': [
        ['event-sessions', 'search'],
        ['events', 'analytics'],
    ],
    files: [
        ['spaces', 'limits'],
        ['organizations'],
        ['organization-profiles'],
        ['users'],
        ['user-profiles'],
        ['organization-profile-jobs'],
        ['organization-profile-users'],
        ['event-sessions'],
        ['event-session-users'],
        ['event-attendees'],
        ['events'],
        ['messages'],
        ['conversations'],
    ],
};

// Deletion can also remove relationships and their cached collections.
const deletionQueryKeys: Record<string, QueryKey[]> = {
    organizations: [['jobs'], ['files'], ['conversations'], ['events', 'list'], ['events', 'analytics']],
    'organization-profiles': [['events', 'list']],
    users: [['user-notification-settings'], ['educations'], ['experiences']],
    events: [
        ['event-attendees'],
        ['event-pages'],
        ['organization-profiles'],
        ['organization-profile-jobs'],
        ['organization-profile-users'],
        ['event-session-users'],
        ['conversations'],
        ['messages'],
        ['scheduled-emails'],
        ['saved-users'],
        ['saved-organizations'],
        ['saved-jobs'],
        ['saved-event-sessions'],
    ],
};

export const resourceInvalidationKeys = (resource: string, operation?: 'create' | 'update' | 'delete'): QueryKey[] => [
    [resource],
    ...(relatedQueryKeys[resource] ?? []),
    ...(operation === 'delete' ? (deletionQueryKeys[resource] ?? []) : []),
];
